import { useEffect, useRef, useState } from 'react';

/**
 * Sticky per-entity flag: becomes true once `isGenerating` is observed as true,
 * and resets when `resetKey` changes.
 *
 * Used to decide whether to animate a result (e.g. a transcription summary) when
 * it arrives — we only want the animation on the generate → complete transition,
 * not when landing on an already-complete result (or just fetching its DTO).
 * Resetting on `resetKey` ensures navigating to a different entity doesn't
 * re-animate its pre-existing result.
 */
const useHasObservedGeneratingTranscription = (
    resetKey: string | undefined,
    isGenerating: boolean,
): boolean => {
    const [hasObservedGenerating, setHasObservedGenerating] =
        useState(isGenerating);
    const observedResetKey = useRef(resetKey);

    // A single effect keeps the two triggers order-independent: when resetKey
    // changes we reseed from the new entity's current state (so navigating to an
    // already-complete result stays false, but landing mid-generation animates);
    // otherwise the flag latches true the moment generation is observed.
    useEffect(() => {
        if (observedResetKey.current !== resetKey) {
            observedResetKey.current = resetKey;
            setHasObservedGenerating(isGenerating);
        } else if (isGenerating) {
            setHasObservedGenerating(true);
        }
    }, [resetKey, isGenerating]);

    return hasObservedGenerating;
};

export default useHasObservedGeneratingTranscription;
